import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EMPTY_TRAIN_LIST_CRITERIA, EMPTY_TRAIN_LIST_FILTERS_FRONT_CRITERIA, EMPTY_TRAIN_LIST_FRONT_FILTER } from "../constants/trainConstants";
import { TrainState } from "../objects/trainState";
import { TrainProduct, TrainService, TrainTrip } from "../objects/train";
import { TrainListCriteria, TrainListFiltersFrontCriteria, TrainListFrontFilters, TrainTravelersFrontCriteria } from "../objects/trainList";
import { searchNextDate } from "../utils/dates/searchNextDate";
import { TrainForm } from "../objects/trainForm";
import { Currency } from "../../../Reducers/objects/currency";
import { TrainBackendTraveler } from "../objects/backendData/trainBackendDate";
import { TrainProvider } from "../objects/backendData/trainProvider";
import { TrainCartFromBackend } from "../objects/backendData/cart/trainCartFromBackend";

const initialState: TrainState = {
    train_trip_list_outbound: [],
    train_trip_list_return: [],
    train_trip_list_return_with_compatible_offers: [],
    train_list_front_filters: EMPTY_TRAIN_LIST_FRONT_FILTER,
    train_travelers: [],
    train_departure_selected: null,
    train_departure_form: null,
    train_return_selected: null,
    train_return_form: null,
    services_available: {},
    products_available: {},
    train_list_refresh: false,
    train_list_loading: false,
    train_list_load_more_loading: false,
    train_list_loaded: false,
    train_list_visible_item: 5,
    train_list_criteria: EMPTY_TRAIN_LIST_CRITERIA,
    unvalidate_filters: EMPTY_TRAIN_LIST_FILTERS_FRONT_CRITERIA,
    train: null,
    currencies: {},
    train_providers: undefined,


    cart: [],
    refresh: null
};


export const smallFlush = createAction('SMALL_FLUSH');
export const cartRefreshPrice = createAction<any>('CART_REFRESH_PRICE');

//  ------------------------------ search ----------------------------------- //
const toggleTrainRefreshListReducer = (state: TrainState): TrainState => ({
    ...state, train_list_refresh: !state.train_list_refresh, train_list_loaded: false
});
const updateTrainListOutboundReducer = (state: TrainState, action: PayloadAction<TrainTrip[]>): TrainState => ({
    ...state, train_trip_list_outbound: action.payload, train_list_loaded: true
});
const updateTrainListReturnReducer = (state: TrainState, action: PayloadAction<TrainTrip[]>): TrainState => ({
    ...state, train_trip_list_return: action.payload, train_list_loaded: true
});
const setTrainTravelersReducer = (state: TrainState, action: PayloadAction<TrainBackendTraveler[]>): TrainState => ({
    ...state, train_travelers: action.payload
});
const updateProductsAvailableReducer = (state: TrainState, action: PayloadAction<Record<string, TrainProduct>>): TrainState => ({
    ...state, products_available: action.payload
});
const updateServicesAvailableReducer = (state: TrainState, action: PayloadAction<Record<string, TrainService>>): TrainState => ({
    ...state, services_available: action.payload
});
const updateTrainListLoadingReducer = (state: TrainState, action: PayloadAction<boolean>): TrainState => ({
    ...state, train_list_loading: action.payload
});
const updateTrainListLoadMoreLoadingReducer = (state: TrainState, action: PayloadAction<boolean>): TrainState => ({
    ...state, train_list_load_more_loading: action.payload
});
const updateTrainListLoadedReducer = (state: TrainState, action: PayloadAction<boolean>): TrainState => ({
    ...state, train_list_loaded: action.payload
});
const updateTrainListVisibleItemReducer = (state: TrainState, action: PayloadAction<number>): TrainState => ({
    ...state, train_list_visible_item: action.payload
});
const setTrainReducer = (state: TrainState, action: PayloadAction<TrainTrip | null>): TrainState => ({
    ...state, train: action.payload
});


const updateTrainListCriteriaReducer = (state: TrainState, action: PayloadAction<TrainListCriteria>): TrainState => ({
    ...state, train_list_criteria: action.payload
});

// --------------- filters --------------- //
const updateTrainListFiltersFrontCriteriaReducer = (state: TrainState, action: PayloadAction<TrainListFiltersFrontCriteria>): TrainState => ({
    ...state, train_list_criteria: { ...state.train_list_criteria, filters: { ...state.train_list_criteria.filters, ...action.payload } }
});
const updateTrainListFiltersSwapDestinationReducer = (state: TrainState): TrainState => ({
    ...state, train_list_criteria: { ...state.train_list_criteria, filters: { ...state.train_list_criteria.filters, origin: state.train_list_criteria.filters.destination, destination: state.train_list_criteria.filters.origin } }
});
const deleteTrainListFlitersFrontReturnReducer = (state: TrainState): TrainState => ({
    ...state, train_list_criteria: { ...state.train_list_criteria, filters: { ...state.train_list_criteria.filters, can_return: false, return: null } }
});
const addTrainListFlitersFrontReturnReducer = (state: TrainState): TrainState => {
    const { return_hour, return_date } = searchNextDate(state.train_list_criteria.filters.departure, state.train_list_criteria.filters.departure_hour);
    return ({
        ...state, train_list_criteria: {
            ...state.train_list_criteria, filters: {
                ...state.train_list_criteria.filters,
                can_return: true,
                return: return_date,
                return_hour
            }
        }
    });
};
const updateTrainListFiltersFrontTravelersReducer = (state: TrainState, action: PayloadAction<TrainTravelersFrontCriteria[]>): TrainState => ({
    ...state, train_list_criteria: { ...state.train_list_criteria, filters: { ...state.train_list_criteria.filters, travelers: action.payload } }
});

// ------------------ unvalidate filter ------------------ //
const updateTrainListFiltersUnvalidateFrontCriteriaReducer = (state: TrainState, action: PayloadAction<TrainListFiltersFrontCriteria>): TrainState => ({
    ...state, unvalidate_filters: { ...state.unvalidate_filters, ...action.payload }
});
const updateTrainListFiltersUnvalidateSwapDestinationReducer = (state: TrainState): TrainState => ({
    ...state, unvalidate_filters: { ...state.unvalidate_filters, origin: state.unvalidate_filters.destination, destination: state.unvalidate_filters.origin }
});
const deleteTrainListFlitersUnvalidateFrontReturnReducer = (state: TrainState): TrainState => ({
    ...state, unvalidate_filters: { ...state.unvalidate_filters, can_return: false, return: null }
});
const addTrainListFlitersUnvalidateFrontReturnReducer = (state: TrainState): TrainState => {
    const { return_hour, return_date } = searchNextDate(state.unvalidate_filters.departure, state.unvalidate_filters.departure_hour);
    return ({
        ...state, unvalidate_filters: {
            ...state.unvalidate_filters,
            can_return: true,
            return: return_date,
            return_hour
        }
    });
};
const updateTrainListFiltersUnvalidateFrontTravelersReducer = (state: TrainState, action: PayloadAction<TrainTravelersFrontCriteria[]>): TrainState => ({
    ...state, unvalidate_filters: { ...state.unvalidate_filters, travelers: action.payload }
});
const updateTrainListFiltersUnvalidateFrontDeleteTravelersErrorsReducer = (state: TrainState): TrainState => ({
    ...state, unvalidate_filters: { ...state.unvalidate_filters, travelers_error: undefined }
})

// ------------------ front filters --------------------- //
const updateTrainListFrontFilterReducer = (state: TrainState, action: PayloadAction<TrainListFrontFilters>): TrainState => ({
    ...state, train_list_front_filters: action.payload
});


// -------------- train form ----------------------- //
const updateTrainDepartureFormReducer = (state: TrainState, action: PayloadAction<Omit<TrainForm, 'travelers'> | null>): TrainState => ({
    ...state, train_departure_form: action.payload ? { ...action.payload, travelers: state.train_travelers } : null
});
const updateTrainReturnFormReducer = (state: TrainState, action: PayloadAction<Omit<TrainForm, 'travelers'> | null>): TrainState => ({
    ...state, train_return_form: action.payload ? { ...action.payload, travelers: state.train_travelers } : null
});

// ----------- currencies ------------ //
const setCurrenciesReducer = (state: TrainState, action: PayloadAction<Currency[]>): TrainState => ({
    ...state, currencies: action.payload.reduce((acc, currency) => ({ ...acc, [currency.id]: currency }), {})
});


// ------------- providers -------------- //
const setTrainProvidersReducer = (state: TrainState, action: PayloadAction<TrainProvider[]>): TrainState => ({
    ...state, train_providers: action.payload
});

// -------------- pre cart ------------ //
const updateTrainDepartureSelectedReducer = (state: TrainState, action: PayloadAction<Omit<TrainForm, 'travelers'> | null>): TrainState => {

    const train_trip_list_return_with_compatible_offers = state.train_trip_list_return.map((trip) => ({
        ...trip,
        solutions: {
            ...trip.solution,
            offers: trip.solution.offers.filter((offer) => action.payload?.offer.compatible_offers?.includes(offer.id))
        }
    }));
    train_trip_list_return_with_compatible_offers.filter((trip) => trip.solutions.offers.length > 0);
    return ({
        ...state,
        train_departure_selected: action.payload ? { ...action.payload, travelers: state.train_travelers } : null,
        train_trip_list_return_with_compatible_offers: train_trip_list_return_with_compatible_offers.filter((trip) => trip.solutions.offers.length > 0)
    })

};

const updateTrainReturnSelectedReducer = (state: TrainState, action: PayloadAction<Omit<TrainForm, 'travelers'> | null>): TrainState => ({
    ...state, train_return_selected: action.payload ? { ...action.payload, travelers: state.train_travelers } : null
});


// ----------- cart ------------ //
const addTrainCartReducer = (state: TrainState, action: PayloadAction<TrainCartFromBackend>): TrainState => ({
    ...state, cart: [...state.cart, action.payload]
});
const setTrainCartReducer = (state: TrainState, action: PayloadAction<TrainCartFromBackend[]>): TrainState => {
    if (state.refresh !== null) {
        const updatedCart = action.payload.map(car => {
            const priceChange = state.refresh[car.id];
            return priceChange !== undefined
                ? { ...car, price_change: priceChange }
                : car;
        });
        return {
            ...state,
            cart: updatedCart
        };
    }
    return ({ ...state, cart: action.payload });
};

const updateTrainCartReducer = (state: TrainState, action: PayloadAction<any>): TrainState => {
    let cart = [...state.cart];
    for (let i = 0; i < cart.length; i++) {
        action.payload.map((item: any) => {
            item.map((new_car: any) => {
                if (cart[i]?.id === new_car.id) {
                    cart[i] = new_car;
                }
            });
        });
    }
    state.cart = cart;
    return state;
};

const editTrainCartByIdReducer = (state: TrainState, action: PayloadAction<TrainCartFromBackend>): TrainState => ({
    ...state, cart: state.cart.map((train) => train.id === action.payload.id ? action.payload : train)
});
const removeTrainCartByIdReducer = (state: TrainState, action: PayloadAction<number>): TrainState => ({
    ...state, cart: state.cart.filter((train) => train.id !== action.payload)
});



const cartRefreshPriceReducer = (state: TrainState, action: PayloadAction<any>): TrainState => ({
    ...state, refresh: action.payload?.train ?? null
});

const smallFlushReducer = (state: TrainState): TrainState => initialState;

export const trainSlice = createSlice({
    name: 'train',
    initialState,
    reducers: {
        toggleTrainRefreshList: toggleTrainRefreshListReducer,
        updateTrainListOutbound: updateTrainListOutboundReducer,
        updateTrainListReturn: updateTrainListReturnReducer,
        setTrainTravelers: setTrainTravelersReducer,
        updateServicesAvailable: updateServicesAvailableReducer,
        updateProductsAvailable: updateProductsAvailableReducer,
        updateTrainListCriteria: updateTrainListCriteriaReducer,

        updateTrainListFiltersFrontCriteria: updateTrainListFiltersFrontCriteriaReducer,
        updateTrainListFiltersSwapDestination: updateTrainListFiltersSwapDestinationReducer,
        deleteTrainListFlitersFrontReturn: deleteTrainListFlitersFrontReturnReducer,
        addTrainListFlitersFrontReturn: addTrainListFlitersFrontReturnReducer,
        updateTrainListFiltersFrontTravelers: updateTrainListFiltersFrontTravelersReducer,

        updateTrainListFrontFilter: updateTrainListFrontFilterReducer,

        updateTrainListFiltersUnvalidateFrontCriteria: updateTrainListFiltersUnvalidateFrontCriteriaReducer,
        updateTrainListFiltersUnvalidateSwapDestination: updateTrainListFiltersUnvalidateSwapDestinationReducer,
        deleteTrainListFlitersUnvalidateFrontReturn: deleteTrainListFlitersUnvalidateFrontReturnReducer,
        addTrainListFlitersUnvalidateFrontReturn: addTrainListFlitersUnvalidateFrontReturnReducer,
        updateTrainListFiltersUnvalidateFrontTravelers: updateTrainListFiltersUnvalidateFrontTravelersReducer,
        updateTrainListFiltersUnvalidateFrontDeleteTravelersErrors: updateTrainListFiltersUnvalidateFrontDeleteTravelersErrorsReducer,


        updateTrainListLoading: updateTrainListLoadingReducer,
        updateTrainListLoadMoreLoading: updateTrainListLoadMoreLoadingReducer,
        updateTrainListLoaded: updateTrainListLoadedReducer,
        updateTrainListVisibleItem: updateTrainListVisibleItemReducer,
        setTrain: setTrainReducer,
        updateTrainDepartureForm: updateTrainDepartureFormReducer,
        updateTrainReturnForm: updateTrainReturnFormReducer,
        updateTrainDepartureSelected: updateTrainDepartureSelectedReducer,
        updateTrainReturnSelected: updateTrainReturnSelectedReducer,
        setCurrencies: setCurrenciesReducer,
        setTrainProviders: setTrainProvidersReducer,

        addTrainCart: addTrainCartReducer,
        setTrainCart: setTrainCartReducer,
        updateTrainCart: updateTrainCartReducer,
        editTrainCartById: editTrainCartByIdReducer,
        removeTrainCartById: removeTrainCartByIdReducer
    },
    extraReducers: (builder) => {
        builder.addCase(cartRefreshPrice, cartRefreshPriceReducer);
        builder.addCase(smallFlush, smallFlushReducer);
    }
});

export const {
    toggleTrainRefreshList,
    updateTrainListOutbound,
    updateTrainListReturn,
    setTrainTravelers,
    updateServicesAvailable,
    updateProductsAvailable,
    updateTrainListCriteria,
    updateTrainListFiltersFrontCriteria,
    updateTrainListFiltersSwapDestination,
    deleteTrainListFlitersFrontReturn,
    addTrainListFlitersFrontReturn,
    updateTrainListFiltersFrontTravelers,
    updateTrainListLoading,
    updateTrainListLoadMoreLoading,
    updateTrainListLoaded,
    updateTrainListVisibleItem,
    setTrain,
    updateTrainDepartureForm,
    updateTrainReturnForm,
    updateTrainDepartureSelected,
    updateTrainReturnSelected,
    setCurrencies,
    setTrainProviders,

    updateTrainListFiltersUnvalidateFrontCriteria,
    updateTrainListFiltersUnvalidateSwapDestination,
    deleteTrainListFlitersUnvalidateFrontReturn,
    addTrainListFlitersUnvalidateFrontReturn,
    updateTrainListFiltersUnvalidateFrontTravelers,
    updateTrainListFiltersUnvalidateFrontDeleteTravelersErrors,

    updateTrainListFrontFilter,

    addTrainCart,
    setTrainCart,
    updateTrainCart,
    editTrainCartById,
    removeTrainCartById
} = trainSlice.actions;
